import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useReveal } from "@/lib/useReveal";
import { ArrowUpRight, ChevronDown, Info, Sparkles } from "lucide-react";

// Decorative SVG placeholder used when a wine has no real photo yet
function LabelPlaceholder({ wine, className = "" }) {
    const initials = wine.variety
        ? wine.variety.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
        : wine.name[0];
    return (
        <div
            data-testid={`wine-placeholder-${wine.id}`}
            className={`relative w-full h-full flex items-center justify-center bg-gradient-to-b from-[#EDEBE6] to-[#D9D3C7] ${className}`}
        >
            <div className="absolute inset-6 border border-burgundy/25"></div>
            <div className="text-center px-6 select-none">
                <div className="font-serif-italic text-burgundy text-base tracking-[0.25em] uppercase mb-3">Štrokay</div>
                <div className="w-12 h-px bg-burgundy/40 mx-auto mb-5"></div>
                <div className="font-serif text-7xl md:text-8xl text-ink/85 leading-none">{initials}</div>
                <div className="font-serif text-2xl md:text-3xl text-ink mt-5 leading-tight">{wine.name}</div>
                <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-ink2">{wine.sweetness} · {wine.year}</div>
            </div>
        </div>
    );
}

function SweetnessBadge({ sweetness, type }) {
    const isRed = type === "červené";
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold border ${
                isRed
                    ? "bg-[#3A171D] text-cream border-[#3A171D]"
                    : "bg-cream text-ink border-ink/15"
            }`}
        >
            <span className={`w-1.5 h-1.5 rounded-full ${isRed ? "bg-cream/70" : "bg-burgundy"}`} />
            {sweetness}
        </span>
    );
}

function LabelInfo({ wine }) {
    const [open, setOpen] = useState(false);
    const has = wine.alcohol || wine.bottler_name || wine.batch || wine.qr_info;
    if (!has) return null;
    return (
        <div className="border-t border-ink/15 mt-5 pt-4">
            <button
                data-testid={`wine-label-toggle-${wine.id}`}
                onClick={() => setOpen(!open)}
                className="flex items-center justify-between w-full text-[11px] uppercase tracking-[0.22em] font-semibold text-ink/70 hover:text-burgundy transition-colors"
            >
                <span className="flex items-center gap-2"><Info size={12} /> Informace z etikety</span>
                <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <dl className="mt-4 space-y-2 text-xs text-ink2 leading-relaxed">
                    {wine.alcohol && <div className="flex justify-between gap-4"><dt className="uppercase tracking-[0.15em] text-ink2/80">Alkohol</dt><dd className="text-ink text-right">{wine.alcohol}</dd></div>}
                    {wine.volume && <div className="flex justify-between gap-4"><dt className="uppercase tracking-[0.15em] text-ink2/80">Objem</dt><dd className="text-ink text-right">{wine.volume}</dd></div>}
                    {wine.origin && <div className="flex justify-between gap-4"><dt className="uppercase tracking-[0.15em] text-ink2/80">Původ</dt><dd className="text-ink text-right">{wine.origin}</dd></div>}
                    {wine.bottler_name && (
                        <div className="flex justify-between gap-4">
                            <dt className="uppercase tracking-[0.15em] text-ink2/80">Plněno v:</dt>
                            <dd className="text-ink text-right">{wine.bottler_name}<br /><span className="text-ink2 text-[11px]">{wine.bottler_address}</span></dd>
                        </div>
                    )}
                    {wine.batch && <div className="flex justify-between gap-4"><dt className="uppercase tracking-[0.15em] text-ink2/80">Šarže</dt><dd className="text-ink text-right">{wine.batch}</dd></div>}
                    {wine.allergens && <div className="flex justify-between gap-4"><dt className="uppercase tracking-[0.15em] text-ink2/80">Alergeny</dt><dd className="text-ink text-right">{wine.allergens}</dd></div>}
                    {wine.qr_info && (
                        <div className="pt-3 border-t border-ink/10 mt-3">
                            <dt className="uppercase tracking-[0.15em] text-ink2/80 mb-1.5">E-etiketa (QR)</dt>
                            <dd className="text-ink text-[12px] leading-relaxed">{wine.qr_info}</dd>
                        </div>
                    )}
                </dl>
            )}
        </div>
    );
}

function WineCard({ w, i, onOrder, visible }) {
    return (
        <article
            data-testid={`wine-card-${w.id}`}
            className={`group relative flex flex-col bg-cream border border-ink/8 transition-all duration-700 hover:border-burgundy/40 hover:shadow-[0_20px_50px_-25px_rgba(30,43,77,0.4)] hover:-translate-y-1 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
            style={{ transitionDelay: `${i * 90}ms` }}
        >
            {/* Ročník - editorial number */}
            <div className="absolute top-5 right-5 z-10 text-right">
                <div className="font-serif-italic text-burgundy text-[11px] tracking-[0.2em] uppercase leading-none opacity-80">Ročník</div>
                <div className="font-serif text-3xl text-ink leading-none mt-1">{w.year}</div>
            </div>

            {/* Image */}
            <div className="image-hover overflow-hidden bg-gradient-to-b from-cream2 to-cream h-[420px] relative flex items-center justify-center">
                {w.image_url ? (
                    <img src={w.image_url} alt={w.name} className="w-full h-full object-contain p-4" loading="lazy" />
                ) : (
                    <LabelPlaceholder wine={w} />
                )}
                <div className="absolute top-5 left-5">
                    <SweetnessBadge sweetness={w.sweetness} type={w.type} />
                </div>
            </div>

            {/* Content */}
            <div className="flex flex-col flex-1 p-7">
                <h3 className="font-serif text-3xl md:text-4xl text-ink leading-tight">{w.name}</h3>
                <div className="font-serif-italic text-burgundy text-lg mt-1 mb-4">{w.variety} · {w.sweetness}</div>
                <p className="text-sm md:text-base text-ink2 leading-relaxed">{w.description}</p>
                <LabelInfo wine={w} />
                <div className="editorial-line my-6" />

                {/* Price + CTA */}
                <div className="mt-auto flex items-end justify-between">
                    <div>
                        <div className="font-serif text-3xl text-ink leading-none">{w.price} <span className="text-lg text-ink2">Kč</span></div>
                        <div className="text-[10px] uppercase tracking-[0.24em] text-ink2 mt-2">{w.volume || "0,75 l"} · {w.alcohol || ""}</div>
                    </div>
                    <button
                        data-testid={`wine-order-${w.id}`}
                        onClick={() => onOrder(w.name)}
                        className="relative inline-flex items-center gap-2 px-5 py-3 bg-burgundy text-cream text-[11px] uppercase tracking-[0.22em] font-semibold overflow-hidden group/btn transition-all duration-300 hover:bg-burgundyDark hover:pr-8"
                    >
                        <span>Objednat</span>
                        <ArrowUpRight size={14} className="transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                    </button>
                </div>
            </div>
        </article>
    );
}

export default function Wines({ onOrder }) {
    const [wines, setWines] = useState([]);
    const [loading, setLoading] = useState(true);
    const { ref, visible } = useReveal();

    useEffect(() => {
        api.get("/wines")
            .then((r) => setWines(r.data))
            .catch(() => setWines([]))
            .finally(() => setLoading(false));
    }, []);

    const featured = wines[0];
    const rest = wines.slice(1);

    return (
        <section
            id="vina"
            ref={ref}
            data-testid="wines-section"
            className="py-24 md:py-36 px-6 md:px-12 bg-cream2 relative"
        >
            <div className="mx-auto max-w-[1500px]">
                <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-end transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                    <div className="lg:col-span-8">
                        <p className="overline mb-6">Vína · Ročníky · Charakter</p>
                        <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-ink">
                            Naše <span className="font-serif-italic text-burgundy">vína</span>
                        </h2>
                        <p className="mt-6 max-w-xl text-base md:text-lg text-ink2 leading-relaxed">
                            Osm vín z naší vinice — sedm bílých a jedno červené. Svěží, lehká, s respektem k odrůdě i kraji.
                        </p>
                    </div>
                    <div className="lg:col-span-4 lg:justify-self-end">
                        <div className="text-right">
                            <div className="font-serif text-6xl md:text-7xl text-burgundy leading-none">{wines.length || "—"}</div>
                            <div className="overline mt-2">Vín v katalogu</div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="wines-loading">
                        {[0, 1, 2].map((k) => (
                            <div key={k} className="bg-cream/40 border border-ink/8 h-[720px] animate-pulse" />
                        ))}
                    </div>
                ) : (
                    <>
                        {/* Featured */}
                        {featured && (
                            <article
                                data-testid={`wine-featured-${featured.id}`}
                                className={`relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-24 bg-cream border border-ink/8 transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                            >
                                {/* Vertical "Doporučujeme" ribbon - outside card, subtle */}
                                <div className="hidden xl:flex absolute -left-12 top-8 flex-col items-center gap-3 text-burgundy">
                                    <Sparkles size={14} />
                                    <span className="[writing-mode:vertical-rl] rotate-180 tracking-[0.4em] text-[10px] uppercase font-semibold whitespace-nowrap">Doporučujeme</span>
                                    <span className="w-px h-8 bg-burgundy/40" />
                                </div>
                                <div className="lg:col-span-7 image-hover overflow-hidden bg-gradient-to-b from-cream2 to-cream h-[420px] md:h-[640px] flex items-center justify-center relative">
                                    {featured.image_url ? (
                                        <img src={featured.image_url} alt={featured.name} className="w-full h-full object-contain p-6" />
                                    ) : (
                                        <LabelPlaceholder wine={featured} />
                                    )}
                                    <div className="absolute top-6 left-6 lg:left-10">
                                        <SweetnessBadge sweetness={featured.sweetness} type={featured.type} />
                                    </div>
                                </div>
                                <div className="lg:col-span-5 flex flex-col justify-center p-8 lg:p-12 lg:pl-0">
                                    <div className="flex items-center justify-between mb-4 gap-4">
                                        <div className="flex items-center gap-3">
                                            <Sparkles size={13} className="text-burgundy" />
                                            <span className="overline">Doporučujeme · Featured 01</span>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="font-serif-italic text-burgundy text-[10px] tracking-[0.2em] uppercase leading-none opacity-80">Ročník</div>
                                            <div className="font-serif text-2xl text-ink leading-none mt-1">{featured.year}</div>
                                        </div>
                                    </div>
                                    <h3 className="font-serif text-5xl md:text-6xl text-ink leading-[0.95] mb-3">
                                        {featured.name}
                                    </h3>
                                    <p className="font-serif-italic text-2xl text-burgundy mb-6">{featured.variety} · {featured.sweetness}</p>
                                    <p className="text-base md:text-lg text-ink2 leading-relaxed mb-6 max-w-md">
                                        {featured.description}
                                    </p>
                                    <LabelInfo wine={featured} />
                                    <div className="editorial-line my-6 max-w-md"></div>
                                    <div className="flex items-end justify-between max-w-md">
                                        <div>
                                            <div className="font-serif text-4xl text-ink leading-none">{featured.price} <span className="text-xl text-ink2">Kč</span></div>
                                            <div className="text-[10px] uppercase tracking-[0.24em] text-ink2 mt-2">{featured.volume || "0,75 l"} · {featured.alcohol || ""}</div>
                                        </div>
                                        <button
                                            data-testid={`wine-order-${featured.id}`}
                                            onClick={() => onOrder(featured.name)}
                                            className="btn-burgundy"
                                        >
                                            Objednat
                                        </button>
                                    </div>
                                </div>
                            </article>
                        )}

                        {/* Rest grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {rest.map((w, i) => (
                                <WineCard key={w.id} w={w} i={i} onOrder={onOrder} visible={visible} />
                            ))}
                        </div>
                    </>
                )}

                <div className="mt-24 p-10 md:p-14 bg-burgundy text-cream relative overflow-hidden">
                    {/* Decorative circles */}
                    <div aria-hidden className="absolute -top-24 -right-24 w-64 h-64 rounded-full border border-cream/10" />
                    <div aria-hidden className="absolute -top-16 -right-16 w-48 h-48 rounded-full border border-cream/15" />
                    <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
                        <div className="md:col-span-2">
                            <p className="overline mb-3 text-cream/70">Doručení po ČR</p>
                            <p className="font-serif text-2xl md:text-3xl text-cream leading-snug max-w-xl">
                                Zasíláme vína po celé České republice. Objednávku domluvíme přes kontaktní formulář.
                            </p>
                        </div>
                        <button
                            data-testid="wines-order-cta"
                            className="md:justify-self-end inline-flex items-center gap-3 bg-cream text-burgundyDeep px-8 py-4 text-[12px] uppercase tracking-[0.24em] font-semibold overflow-hidden group transition-all duration-300 hover:pr-11"
                            onClick={() => onOrder("vinný balíček")}
                        >
                            <span>Objednat víno</span>
                            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}
